<script>
import TrainingStatusTag from '@app/components/TrainingStatusTag.vue';
import BundleStatusTag from '@app/components/BundleStatusTag.vue';
import PropagationConfirmModal from '@app/components/PropagationConfirmModal.vue';
import Draggable from 'vuedraggable';
import APIMemberships from '@app/services/API/Memberships';

export default {
  components: {
    TrainingStatusTag, BundleStatusTag, Draggable,
  },
  async created() {
    this.isFetching = true;
    await this.$store.dispatch('trainings/resetSearchParams');
    await this.$store
      .dispatch('trainings/fetch')
      .finally(() => (this.isFetching = false));
  },
  data() {
    return {
      isFetching: false,
      isProcessing: false,
      localItems: [],
    };
  },
  computed: {
    membership() {
      return this.$store.state.memberships.current;
    },
    membershipItems() {
      if (!this.membership || !this.membership.items) {
        return [];
      }
      return [...this.membership.items].sort((a, b) => {
        const orderA = typeof a.order === 'number' ? a.order : 0;
        const orderB = typeof b.order === 'number' ? b.order : 0;
        return orderA - orderB;
      });
    },
    trainings() {
      const trainingsState = this.$store.state.trainings;
      if (trainingsState && trainingsState.list && Array.isArray(trainingsState.list.data)) {
        return trainingsState.list.data;
      }
      return [];
    },
    hasActiveSubscribers() {
      return this.membership && this.membership.active_subscribers_count > 0;
    },
    notIncludedTrainings() {
      return this.trainings
        .filter((training) => {
          const isIncluded = this.membershipItems.some((item) => (
            this.isTrainingType(item.buyable_type)
              && item.buyable
              && item.buyable.uuid === training.uuid
          ));
          return !isIncluded;
        })
        .filter((training) => training.status !== 'DRAFT');
    },
  },
  watch: {
    membershipItems: {
      handler(newItems) {
        this.localItems = newItems.map((item) => ({ ...item }));
      },
      immediate: true,
    },
  },
  methods: {
    isTrainingType(type) {
      return type === 'TRAINING' || type === 'App\\Training';
    },
    isTrainingIncluded(training) {
      return this.membershipItems.some((item) => (
        this.isTrainingType(item.buyable_type)
          && item.buyable
          && item.buyable.uuid === training.uuid
      ));
    },
    async addTraining(training) {
      if (this.isProcessing || !this.membership) {
        return;
      }

      if (this.isTrainingIncluded(training)) {
        return;
      }

      if (this.hasActiveSubscribers) {
        this.$buefy.dialog.confirm({
          title: 'Ajouter cette formation',
          message: `Êtes-vous sûr de vouloir ajouter "${training.name}" à ce plan d'abonnement ?`,
          type: 'is-primary',
          hasIcon: true,
          confirmText: 'Confirmer',
          cancelText: 'Annuler',
          onConfirm: () => this.executeAddTraining(training),
        });
      } else {
        await this.executeAddTraining(training);
      }
    },
    async executeAddTraining(training) {
      this.isProcessing = true;

      try {
        let propagate = false;

        if (this.hasActiveSubscribers) {
          const result = await this.confirmPropagation();
          if (result === null) {
            this.isProcessing = false;
            return;
          }
          propagate = result;
        }

        await APIMemberships.addTraining(this.membership.uuid, training.uuid, propagate);
        await this.$store.dispatch('memberships/find', this.membership.uuid);
        this.$showMessage.success('Formation ajoutée avec succès');
      } catch (error) {
        this.$showMessage.error(this.resolveErrorMessage(error));
      } finally {
        this.isProcessing = false;
      }
    },
    async removeItem(item) {
      if (this.isProcessing || !this.membership || !item) {
        return;
      }

      if (this.hasActiveSubscribers) {
        const itemName = this.getItemName(item);

        this.$buefy.dialog.confirm({
          title: 'Retirer ce contenu',
          message: `Êtes-vous sûr de vouloir retirer "${itemName}" de ce plan d'abonnement ?`,
          type: 'is-primary',
          hasIcon: true,
          confirmText: 'Confirmer',
          cancelText: 'Annuler',
          onConfirm: () => this.executeRemoveItem(item),
        });
      } else {
        await this.executeRemoveItem(item);
      }
    },
    async executeRemoveItem(item) {
      this.isProcessing = true;

      try {
        let propagate = false;

        if (this.hasActiveSubscribers) {
          const result = await this.confirmPropagation();
          if (result === null) {
            this.isProcessing = false;
            return;
          }
          propagate = result;
        }

        const buyableUuid = this.extractBuyableUuid(item);

        if (this.isTrainingType(item.buyable_type)) {
          await APIMemberships.removeTraining(this.membership.uuid, buyableUuid, propagate);
        } else {
          await APIMemberships.removeBundle(this.membership.uuid, buyableUuid, propagate);
        }

        await this.$store.dispatch('memberships/find', this.membership.uuid);
        this.$showMessage.success('Contenu retiré avec succès');
      } catch (error) {
        this.$showMessage.error(this.resolveErrorMessage(error));
      } finally {
        this.isProcessing = false;
      }
    },
    async handleReorder() {
      if (this.isProcessing || !this.membership) {
        return;
      }

      this.isProcessing = true;

      try {
        const reorderData = {
          items: this.localItems.map((item, index) => ({
            uuid: item.uuid,
            order: index,
          })),
        };

        await APIMemberships.reorderMembershipItems(this.membership.uuid, reorderData);
        await this.$store.dispatch('memberships/find', this.membership.uuid);
        this.$showMessage.success('Ordre mis à jour avec succès');
      } catch (error) {
        this.$showMessage.error(this.resolveErrorMessage(error));
        await this.$store.dispatch('memberships/find', this.membership.uuid);
      } finally {
        this.isProcessing = false;
      }
    },
    confirmPropagation() {
      return new Promise((resolve) => {
        const modal = this.$buefy.modal.open({
          parent: this,
          component: PropagationConfirmModal,
          hasModalCard: false,
          trapFocus: true,
          canCancel: ['escape'],
          onCancel: () => resolve(null),
          props: {
            onSelect: (value) => {
              resolve(value);
              modal.close();
            },
          },
        });
      });
    },
    itemKey(item, index) {
      const base = this.extractBuyableUuid(item);
      const type = item && item.buyable_type ? item.buyable_type : 'item';
      return `${type}-${base || index}`;
    },
    getItemTypeLabel(buyableType) {
      return this.isTrainingType(buyableType) ? 'Formation' : 'Pack';
    },
    getItemTypeIcon(buyableType) {
      return this.isTrainingType(buyableType) ? 'graduation-cap' : 'box';
    },
    getItemName(item) {
      if (item && item.buyable && item.buyable.name) {
        return item.buyable.name;
      }
      if (item && item.buyable_name) {
        return item.buyable_name;
      }
      return 'Contenu';
    },
    extractBuyableUuid(item) {
      if (!item) {
        return null;
      }

      if (item.buyable && item.buyable.uuid) {
        return item.buyable.uuid;
      }

      if (item.buyable_uuid) {
        return item.buyable_uuid;
      }

      return null;
    },
    resolveErrorMessage(error) {
      const response = error && error.response;

      if (!response) {
        return 'Une erreur est survenue';
      }

      const data = response.data || {};

      if (response.status === 422 && data.errors) {
        const { errors } = data;
        const keys = Object.keys(errors);

        for (let i = 0; i < keys.length; i += 1) {
          const entry = errors[keys[i]];
          if (Array.isArray(entry) && entry.length > 0) {
            return entry[0];
          }
        }
      }

      if (data.message) {
        return data.message;
      }

      return 'Une erreur est survenue';
    },
  },
};
</script>

<template>
  <div>
    <section class="pb-7">
      <h2 class="is-size-4 has-text-weight-semibold py-3">
        Organisez le contenu de votre abonnement
      </h2>

      <div class="box">
        <table class="table is-fullwidth">
          <thead>
            <tr>
              <th></th>
              <th>Titre du contenu</th>
              <th>Type</th>
              <th>Statut</th>
              <th></th>
            </tr>
          </thead>

          <Draggable
            v-if="localItems.length > 0"
            v-model="localItems"
            tag="tbody"
            handle=".handle"
            :disabled="isProcessing"
            @end="handleReorder"
          >
            <tr
              v-for="(item, index) in localItems"
              :key="itemKey(item, index)"
            >
              <td class="handle valign-middle">
                <b-icon icon="bars" class="cursor-pointer" />
              </td>
              <td class="valign-middle">
                {{ getItemName(item) }}
              </td>
              <td class="valign-middle">
                <span class="tag is-light">
                  <b-icon
                    :icon="getItemTypeIcon(item.buyable_type)"
                    class="mr-1"
                  />
                  {{ getItemTypeLabel(item.buyable_type) }}
                </span>
              </td>
              <td class="valign-middle">
                <TrainingStatusTag
                  v-if="isTrainingType(item.buyable_type)"
                  :status="item.buyable.status"
                />
                <BundleStatusTag v-else :status="item.buyable.status" />
              </td>
              <td
                class="is-flex is-fullwidth is-justify-content-end"
                style="gap: 8px;"
              >
                <router-link
                  v-if="isTrainingType(item.buyable_type)"
                  tag="b-button"
                  :to="{
                    name: 'training',
                    params: { uuid: item.buyable.uuid }
                  }"
                  class="is-small"
                >
                  <b-icon icon="eye" class="mr-1" />
                  Voir la formation
                </router-link>
                <router-link
                  v-else
                  tag="b-button"
                  :to="{ name: 'bundle', params: { uuid: item.buyable.uuid } }"
                  class="is-small"
                >
                  <b-icon icon="eye" class="mr-1" />
                  Voir l'abonnement
                </router-link>
                <b-button
                  native-type="button"
                  type="is-danger"
                  size="is-small"
                  :disabled="isProcessing"
                  @click="removeItem(item)"
                >
                  <b-icon icon="trash" />
                </b-button>
              </td>
            </tr>
          </Draggable>

          <tbody v-else>
            <tr class="is-empty">
              <td colspan="5">
                <p class="p-5 has-text-centered">
                  Aucun contenu
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="pb-7">
      <h2 class="is-size-4 has-text-weight-semibold py-3">
        Ajoutez de nouvelles formations
      </h2>

      <div class="box">
        <table class="table is-fullwidth">
          <thead>
            <tr>
              <th>Titre de la formation</th>
              <th>Statut</th>
              <th></th>
            </tr>
          </thead>

          <tbody v-if="notIncludedTrainings.length > 0">
            <tr v-for="training in notIncludedTrainings" :key="training.uuid">
              <td class="valign-middle">
                {{ training.name }}
              </td>
              <td class="valign-middle">
                <TrainingStatusTag :status="training.status" />
              </td>
              <td
                class="is-flex is-fullwidth is-justify-content-end"
                style="gap: 8px;"
              >
                <router-link
                  tag="b-button"
                  :to="{ name: 'training', params: { uuid: training.uuid } }"
                  class="is-small"
                >
                  <b-icon icon="eye" class="mr-1" />
                  Voir la formation
                </router-link>
                <b-button
                  native-type="button"
                  type="is-primary"
                  size="is-small"
                  :disabled="isProcessing"
                  :loading="isProcessing"
                  @click="addTraining(training)"
                >
                  <b-icon icon="plus" />
                </b-button>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr class="is-empty">
              <td colspan="3">
                <p class="p-5 has-text-centered">
                  Aucune formation disponible
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

  </div>
</template>
